import { Injectable } from '@angular/core';
import { TeamRostersReportFields } from './team-rosters-report-fields';
import { BaseODataService } from 'src/app/shared/services/base-odata.service';
import {
  TeamRostersReportFilterResult,
  TeamRostersChartNumbers,
} from './team-rosters-report.model';
import { AttributeDefinition } from 'src/app/shared/models/attributes/attributeDefinition';
import { AttributeEntityType } from 'src/app/shared/models/attributes/attributeEntityType';
import { AttributeUiType } from 'src/app/shared/models/attributes/attributeUiType';
import {
  FilterGroup,
  ODataFilterValues,
  ODataSubFilter,
} from 'src/app/shared/components/odata-filter/models/odata-filter';
import { CustomFieldFilterModel } from 'src/app/shared/odata-filter-models/custom-field-filter-model';
import { TEAM_ROSTERS_REPORT_FILTER_MODELS } from 'src/app/shared/components/odata-filter/models/system-filter-models';
import { FilterResult } from 'src/app/shared/components/odata-filter/models/filter-result';
import { ViewContext } from 'src/app/shared/components/odata-filter/models/view-context';
import { Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { LookupService } from 'src/app/shared/services/lookup.service';
import { TagService } from 'src/app/shared/services/tag.service';
import buildQuery from 'odata-query';
import { ApiVersions } from 'src/app/shared/services/api-version';
import { SelectedFilter } from 'src/app/shared/components/odata-filter/models/selected-filter';
import { FilterOperator } from 'src/app/shared/components/odata-filter/models/operators';
import { FilterOperatorType } from 'src/app/shared/components/odata-filter/models/filter-operator-type';
import { SeatStatus } from 'src/app/shared/models/team/seatStatus';
import { ComplexFieldsFilterModel } from 'src/app/shared/odata-filter-models/complex-fields-filter-model';
import { SubConditionFilterModel } from 'src/app/shared/odata-filter-models/sub-condition-filter-model';

@Injectable({
  providedIn: 'root',
})
export class TeamRostersReportService extends BaseODataService {
  protected _chartResultNumbers: FilterResult;
  chartNumbers: TeamRostersChartNumbers;
  public readonly chartResultNumbers = new Subject<TeamRostersChartNumbers>();

  constructor(private lookupService: LookupService, private tagService: TagService) {
    super();
    this._baseUrl = 'teamroster';

    this._filterResult = new TeamRostersReportFilterResult();
    this._viewContext = ViewContext.TeamRosters;
    this._chartResult = new TeamRostersReportFilterResult();
    this._chartResultNumbers = new TeamRostersReportFilterResult();

    this._filterCollection = [
      TEAM_ROSTERS_REPORT_FILTER_MODELS.TeamNameFilterModel,
      TEAM_ROSTERS_REPORT_FILTER_MODELS.TeamStatusFilterModel,
      TEAM_ROSTERS_REPORT_FILTER_MODELS.TeamStartDateFilterModel,
      TEAM_ROSTERS_REPORT_FILTER_MODELS.TeamEndDateFilterModel,
      TEAM_ROSTERS_REPORT_FILTER_MODELS.PersonNameFilterModel,
      TEAM_ROSTERS_REPORT_FILTER_MODELS.PersonEmailFilterModel,
      TEAM_ROSTERS_REPORT_FILTER_MODELS.PersonExternalSystemIdFilterModel,
      TEAM_ROSTERS_REPORT_FILTER_MODELS.PersonCompayFilterModel,
      TEAM_ROSTERS_REPORT_FILTER_MODELS.PersonJobTitleFilterModel,
      TEAM_ROSTERS_REPORT_FILTER_MODELS.PersonGenderFilterModel,
      TEAM_ROSTERS_REPORT_FILTER_MODELS.PersonRaceFilterModel,
      TEAM_ROSTERS_REPORT_FILTER_MODELS.PersonTagFilterModel,
      TEAM_ROSTERS_REPORT_FILTER_MODELS.ProfileStatusFilterModel,
      TEAM_ROSTERS_REPORT_FILTER_MODELS.PositionName,
      TEAM_ROSTERS_REPORT_FILTER_MODELS.PositionStatusFilterModel,
      TEAM_ROSTERS_REPORT_FILTER_MODELS.PositionUpdateDateFilterModel,
      TEAM_ROSTERS_REPORT_FILTER_MODELS.SeatStartDateFilterModel,
      TEAM_ROSTERS_REPORT_FILTER_MODELS.SeatEndDateFilterModel,
      TEAM_ROSTERS_REPORT_FILTER_MODELS.SkillsFilterModel,
      TEAM_ROSTERS_REPORT_FILTER_MODELS.PersonEmailPreferenceFilterModel,
    ];

    if (this.hasCertMetricsIntegration) {
      this._filterCollection.push(
        TEAM_ROSTERS_REPORT_FILTER_MODELS.PersonCertMetricsCandidateIdFilterModel
      );
    }

    this.setCustomFields();
    this.setPersonComplexCustomFields();
    this.setOtherFilters();
  }

  protected setOrderBy(value: string[]) {
    if (!value.find((e) => e.includes(TeamRostersReportFields.seatId))) {
      value.push(TeamRostersReportFields.seatId);
    }
    super.setOrderBy(value);
  }

  buildFilter(appliedFilters: SelectedFilter[]) {
    let statusFilter = new SelectedFilter({
      filter: TEAM_ROSTERS_REPORT_FILTER_MODELS.PositionStatusFilterModel,
      filterOperator: new FilterOperator('ne', FilterOperatorType.ne, FilterOperatorType.ne, true),
      searchValue: SeatStatus[SeatStatus.Bench],
    });

    let _appliedFilters = appliedFilters.slice();
    _appliedFilters.push(statusFilter);
    return super.buildFilter(_appliedFilters);
  }

  applyExistingFilters() {
    this.applyFilter(this._appliedFilters);
  }

  private setCustomFields() {
    this.attributeService.attributes
      .pipe(
        map((attributes) => {
          if (!attributes) return null;
          return attributes.filter((a) => a.filterable && a.uiType != AttributeUiType.Complex);
        })
      )
      .subscribe((result: AttributeDefinition[]) => {
        if (result && result.length > 0) {
          let customFields = result.filter(
            (field) =>
              field.entityType != AttributeEntityType.Survey &&
              field.entityType != AttributeEntityType.Evaluation
          );

          // Fills out skills filter values
          let skillsFilterModel = TEAM_ROSTERS_REPORT_FILTER_MODELS.SkillsFilterModel;
          skillsFilterModel.values = result
            .filter(
              (x) => x.entityType == AttributeEntityType.Person && x.uiType == AttributeUiType.Skill
            )
            .map((x) => new ODataFilterValues(x.id, x.name));
          if (this.systemFilterIndex(skillsFilterModel) != -1) {
            this._filterCollection[this.systemFilterIndex(skillsFilterModel)].values =
              skillsFilterModel.values;
          } else {
            super.setCustomFieldFilterCollection(skillsFilterModel);
          }

          //Adds customField filters
          customFields.forEach((item) => {
            const searchValueType = AttributeDefinition.getSearchValueType(item);
            if (!searchValueType) {
              return;
            }

            let targetField: TeamRostersReportFields;
            let filterGroup: FilterGroup;

            if (item.entityType == AttributeEntityType.Person) {
              targetField = TeamRostersReportFields.personCustomFields;
              filterGroup =
                item.uiType == AttributeUiType.Skill
                  ? FilterGroup.PersonSkills
                  : FilterGroup.PersonCustomFields;
            } else if (item.entityType == AttributeEntityType.Team) {
              targetField = TeamRostersReportFields.teamCustomFields;
              filterGroup = FilterGroup.TeamCustomFields;
            } else {
              targetField = TeamRostersReportFields.trackedCustomFields;
              filterGroup = FilterGroup.TrackedFields;
            }

            let customFieldFilterModel = new CustomFieldFilterModel(
              filterGroup,
              item.name,
              targetField,
              item.id,
              searchValueType,
              item.valueList.map((x) => new ODataFilterValues(x.id, x.itemValue))
            );

            super.setCustomFieldFilterCollection(customFieldFilterModel);
          });
        }
        this.filterCollection.next(this._filterCollection);
      });
  }

  private setPersonComplexCustomFields() {
    this.attributeService.attributes
      .pipe(
        map((attributes) => {
          if (!attributes) return null;
          return attributes.filter(
            (a) =>
              a.entityType == AttributeEntityType.Person &&
              a.filterable &&
              a.uiType == AttributeUiType.Complex
          );
        })
      )
      .subscribe((complexFields: AttributeDefinition[]) => {
        if (complexFields && complexFields.length > 0) {
          complexFields.forEach((complexField) => {
            let complexCustomFieldFilterModel = new ComplexFieldsFilterModel(
              complexField.name,
              TeamRostersReportFields.personComplexCustomFields,
              complexField.id
            );
            let subFieldFilters = complexField.subFieldList
              .filter((x) => x.subFieldDefinition.filterable)
              .map((subField) => {
                const searchValueType = AttributeDefinition.getSearchValueType(
                  subField.subFieldDefinition
                );
                if (!searchValueType) {
                  return;
                }

                return new SubConditionFilterModel(
                  subField.subFieldDefinition.name,
                  'values',
                  searchValueType,
                  subField.subFieldDefinition.valueList.map(
                    (x) => new ODataFilterValues(x.id, x.itemValue)
                  ),
                  subField.subFieldDefinition.id
                );
              });

            complexCustomFieldFilterModel.subFilter = new ODataSubFilter(subFieldFilters);
            super.setCustomFieldFilterCollection(complexCustomFieldFilterModel);
          });
        }
        this.filterCollection.next(this._filterCollection);
      });
  }

  getChartNumbers(select: string[], expand: string[]) {
    let filter = this.buildFilter(this._appliedFilters);

    let filterQueryString = buildQuery<any>({
      select: select,
      filter,
      expand: expand,
    });
    let path = this.allResultsFilterUrl.concat(filterQueryString);
    this.get(path, true, ApiVersions.unversioned)
      .pipe(
        map((response) => {
          if (response && response.value) {
            return this._chartResultNumbers.parse(response);
          } else {
            return null;
          }
        })
      )
      .subscribe((data) => {
        if (data) {
          this._chartResultNumbers = data;
          let chartNumbers = new TeamRostersChartNumbers();
          chartNumbers.uniquePersonIdCount = new Set(
            this._chartResultNumbers.values.map((obj) => obj.personId)
          ).size;
          chartNumbers.uniqueTeamIdCount = new Set(
            this._chartResultNumbers.values.map((obj) => obj.teamId)
          ).size;
          chartNumbers.seatIdCount = this._chartResultNumbers.values.filter(
            (obj) => obj.seatId
          ).length;
          this.chartResultNumbers.next(chartNumbers);
        }
      });
  }

  private setOtherFilters() {
    this.lookupService.races.subscribe((result) => {
      if (!result) return;
      let rcaeFilterModel = TEAM_ROSTERS_REPORT_FILTER_MODELS.PersonRaceFilterModel;
      rcaeFilterModel.values = result.map((x) => new ODataFilterValues(x.id, x.name));
      if (this.systemFilterIndex(rcaeFilterModel) != -1) {
        this._filterCollection[this.systemFilterIndex(rcaeFilterModel)].values =
          rcaeFilterModel.values;
      } else {
        this._filterCollection.push(rcaeFilterModel);
      }
    });

    this.tagService.tags.subscribe((result) => {
      if (!result || !result.length) return;
      let model = TEAM_ROSTERS_REPORT_FILTER_MODELS.PersonTagFilterModel;
      model.values = result.map((x) => new ODataFilterValues(x.text, x.text));
      if (this.systemFilterIndex(model) != -1) {
        this._filterCollection[this.systemFilterIndex(model)].values = model.values;
      } else {
        this._filterCollection.push(model);
      }
    });

    this.workspaceService.currentWorkspaces.subscribe((result) => {
      if (!result || !result.workspaces.length || result.activeWorkspace.id != null) return;
      if (result.workspaces.length == 1 && result.workspaces[0].id == null) return; // if the only workspace is org.
      let teamWorkspaceFilterModel = TEAM_ROSTERS_REPORT_FILTER_MODELS.TeamWorkspaceFilterModel;
      teamWorkspaceFilterModel.values = result.workspaces.map(
        (x) => new ODataFilterValues(x.id, x.name)
      );
      if (this.systemFilterIndex(teamWorkspaceFilterModel) != -1) {
        this._filterCollection[this.systemFilterIndex(teamWorkspaceFilterModel)].values =
          teamWorkspaceFilterModel.values;
      } else {
        this._filterCollection.push(teamWorkspaceFilterModel);
      }
    });
  }
}
